import React from "react";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";

import { cn } from "@/lib/utils";
import { ButtonShadcn as Button, buttonVariants } from "@/components/ui/Button";

/**
 * Calendar component built on top of react-day-picker.
 *
 * @param {string} [className] - Additional class names for the root element.
 * @param {Object} [classNames] - Custom class name overrides.
 * @param {boolean} [showOutsideDays=true] - Show days outside the current month.
 * @param {"label" | "dropdown"} [captionLayout="label"] - Layout for the month caption.
 * @param {string} [buttonVariant="ghost"] - Variant for navigation buttons.
 * @param {Object} [locale] - Locale configuration.
 * @param {Object} [formatters] - Custom formatters for date display.
 * @param {Object} [components] - Custom component overrides.
 * @param {...any} props - Additional props passed to DayPicker.
 */
function Calendar({ className, classNames, showOutsideDays = true, captionLayout = "label", buttonVariant = "ghost", locale, formatters, components, ...props }) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "group/calendar bg-background p-2 [--cell-radius:var(--radius-md)] [--cell-size:theme(spacing.7)]",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      locale={locale}
      formatters={{
        /**
         * Format month dropdown label
         * @param {Date} date
         * @returns {string}
         */
        formatMonthDropdown: (date) => date.toLocaleString(locale?.code, { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn("relative flex flex-col gap-4 md:flex-row", defaultClassNames.months),
        month: cn("flex w-full flex-col gap-4 text-center", defaultClassNames.month),
        nav: cn("absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1", defaultClassNames.nav),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) p-0 hover:bg-background-icon/60 w-8 h-8 rounded-full",
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) p-0 hover:bg-background-icon/60 w-8 h-8 rounded-full",
          defaultClassNames.button_next,
        ),
        month_caption: "h-8 flex items-center justify-center text-[16px]",
        // selected
        ...classNames,
      }}
      components={{
        // Custom root wrapper
        Root: ({ className, rootRef, ...props }) => <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />,

        // Custom chevron icons for navigation
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return <ChevronLeftIcon className={cn("size-4", className)} {...props} />;
          }

          if (orientation === "right") {
            return <ChevronRightIcon className={cn("size-4", className)} {...props} />;
          }

          return <ChevronDownIcon className={cn("size-4", className)} {...props} />;
        },

        // Custom day button renderer
        DayButton: (props) => <CalendarDayButton locale={locale} {...props} />,

        // Custom week number cell
        WeekNumber: ({ children, ...props }) => (
          <td {...props}>
            <div className="flex items-center justify-center">{children}</div>
          </td>
        ),

        ...components,
      }}
      {...props}
    />
  );
}

/**
 * Custom Day Button component for each calendar cell.
 *
 * @param {string} [className] - Additional class names.
 * @param {Object} day - Day object from react-day-picker.
 * @param {Object} modifiers - State modifiers (selected, range, etc.).
 * @param {Object} [locale] - Locale configuration.
 * @param {...any} props - Additional props passed to Button.
 */
function CalendarDayButton({ className, day, modifiers, locale, ...props }) {
  const defaultClassNames = getDefaultClassNames();
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString(locale?.code)}
      data-selected-single={modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle}
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      // className={cn(defaultClassNames.day, className)}
      className={cn(
        defaultClassNames.day,
        "h-8 w-8 p-0 font-normal",

        // SELECTED DAY (main fix)
        modifiers.selected && "!bg-button-primary !text-text-tertiary border-0",

        // TODAY
        modifiers.today && "border border-border-primary",

        // OUTSIDE DAYS
        modifiers.outside && "text-text-muted opacity-50",

        className,
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
